/**
 * Partition 1-D points into groups whose rendered (mean) positions stay at least `minMeanGap`
 * apart — for marker rails and similar strips where each group renders one trigger at the mean
 * of its members and adjacent triggers must not overlap.
 *
 * Merges the closest adjacent pair first (leftmost on ties; gaps compare at a sub-pixel
 * quantum so float noise never decides one) and stops once every adjacent pair of means clears
 * the gap: the stop condition is exactly the no-overlap guarantee. A single
 * left-to-right scan is order-biased — it can orphan a near-coincident pair across a group
 * boundary and lets a right-skewed group render within hit range of the next trigger. Merging
 * only pulls adjacent means further apart, so an evenly spaced run still splits instead of
 * chaining viewport-wide.
 *
 * Trade-off: a group's member span may exceed `minMeanGap` when the two constraints conflict
 * (e.g. points 0/26/29 with gap 28 admit no partition that bounds the span AND separates the
 * means). Non-overlap of rendered triggers is the invariant that holds; span growth stays
 * bounded because the mean trails the frontier.
 *
 * Candidate gaps live in a lazily-invalidated min-heap over neighbor-linked groups, keeping a
 * dense collapse O(n log n) — rescanning every adjacent pair after each merge is quadratic
 * exactly when thousands of co-located points fold into one trigger, and this runs per render
 * during viewport pans.
 */
export function clusterByMeanDistance<T>(
  points: readonly T[],
  minMeanGap: number,
  positionOf: (point: T) => number,
): T[][] {
  const sorted = [...points].sort((left, right) => positionOf(left) - positionOf(right));
  if (sorted.length === 0) return [];
  const threshold = Math.max(0, minMeanGap);

  // Adjacent-only merging keeps every group a contiguous run of the sorted array, so a group is
  // just an index range; prefix sums make any range mean O(1) without per-merge member copies.
  const prefixSums: number[] = [0];
  for (const [index, point] of sorted.entries()) {
    prefixSums.push((prefixSums[index] ?? 0) + positionOf(point));
  }
  const meanOf = (group: MergeGroup) =>
    ((prefixSums[group.end + 1] ?? 0) - (prefixSums[group.start] ?? 0)) /
    (group.end + 1 - group.start);

  let head: MergeGroup | null = null;
  let previous: MergeGroup | null = null;
  for (let index = 0; index < sorted.length; index += 1) {
    const group: MergeGroup = {
      start: index,
      end: index,
      version: 0,
      alive: true,
      previous,
      next: null,
    };
    if (previous) previous.next = group;
    else head = group;
    previous = group;
  }

  const heap: GapEntry[] = [];
  const pushAdjacentGap = (left: MergeGroup, right: MergeGroup) => {
    pushGap(heap, {
      // Quantized: sub-pixel gap differences are visual noise, but exact comparison lets float
      // dust decide ties — a uniform run (equal spacing in time, unequal by ulps after the px
      // mapping) then merges like a random matching and lands jagged, unevenly sized groups.
      // Quantized ties fall back to the leftmost rule, folding a uniform run deterministically.
      // The stop check shares the quantum; its worst-case shortfall (half a quantum) sits well
      // inside the cluster distance's slack.
      gap: Math.round((meanOf(right) - meanOf(left)) / GAP_QUANTUM_PX) * GAP_QUANTUM_PX,
      leftStart: left.start,
      left,
      right,
      leftVersion: left.version,
      rightVersion: right.version,
    });
  };
  for (let group = head; group?.next; group = group.next) {
    pushAdjacentGap(group, group.next);
  }

  for (let entry = popGap(heap); entry; entry = popGap(heap)) {
    const { left, right } = entry;
    // Lazy invalidation: a stale entry outlives a merge that changed either endpoint's mean.
    const stale =
      !left.alive ||
      !right.alive ||
      left.next !== right ||
      left.version !== entry.leftVersion ||
      right.version !== entry.rightVersion;
    if (stale) continue;
    // Valid entries carry exact gaps, so the first one at or past the threshold ends the run.
    if (entry.gap >= threshold) break;

    left.end = right.end;
    left.version += 1;
    right.alive = false;
    left.next = right.next;
    if (right.next) right.next.previous = left;
    if (left.previous) pushAdjacentGap(left.previous, left);
    if (left.next) pushAdjacentGap(left, left.next);
  }

  // The leftmost group only ever absorbs — it is never a merge's right side — so `head` survives.
  const groups: T[][] = [];
  for (let group = head; group; group = group.next) {
    groups.push(sorted.slice(group.start, group.end + 1));
  }
  return groups;
}

const GAP_QUANTUM_PX = 0.5;

interface MergeGroup {
  start: number;
  /** Inclusive end index into the sorted array. */
  end: number;
  version: number;
  alive: boolean;
  previous: MergeGroup | null;
  next: MergeGroup | null;
}

interface GapEntry {
  gap: number;
  /** Stable tie-break: equal gaps merge leftmost-first, matching a left-to-right minimum scan. */
  leftStart: number;
  left: MergeGroup;
  right: MergeGroup;
  leftVersion: number;
  rightVersion: number;
}

function gapPrecedes(left: GapEntry, right: GapEntry): boolean {
  return left.gap < right.gap || (left.gap === right.gap && left.leftStart < right.leftStart);
}

function pushGap(heap: GapEntry[], entry: GapEntry): void {
  heap.push(entry);
  let index = heap.length - 1;
  while (index > 0) {
    const parentIndex = (index - 1) >> 1;
    const parent = heap[parentIndex];
    const child = heap[index];
    if (!parent || !child || !gapPrecedes(child, parent)) return;
    heap[parentIndex] = child;
    heap[index] = parent;
    index = parentIndex;
  }
}

function popGap(heap: GapEntry[]): GapEntry | undefined {
  const top = heap[0];
  if (top === undefined) return undefined;
  const last = heap.pop();
  if (last === undefined || heap.length === 0) return top;

  heap[0] = last;
  let index = 0;
  for (;;) {
    const leftIndex = index * 2 + 1;
    const rightIndex = leftIndex + 1;
    let smallest = index;
    const leftChild = heap[leftIndex];
    const rightChild = heap[rightIndex];
    const current = heap[smallest];
    if (current && leftChild && gapPrecedes(leftChild, current)) smallest = leftIndex;
    const smallestEntry = heap[smallest];
    if (smallestEntry && rightChild && gapPrecedes(rightChild, smallestEntry)) {
      smallest = rightIndex;
    }
    if (smallest === index) return top;
    const swap = heap[smallest];
    const held = heap[index];
    if (!swap || !held) return top;
    heap[smallest] = held;
    heap[index] = swap;
    index = smallest;
  }
}
